import React, { useEffect, useState } from 'react'
import { Alert, Button } from '@mui/material';

import { ITrailer } from '../../types/trailer';

import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';
import { TrailerService } from '../../services/trailerService';

export interface ITrailerTableProps {
	data?: ITrailer[];
	customer?: string;
	transporter?: string;
	vendor?: string;
}

const TrailerTable: React.FunctionComponent<ITrailerTableProps> = ({ data, customer, transporter, vendor }) => {
	const [dataView, setDataView] = useState<ITrailer[]>([]);
	const [firstVisibleRow, setFirstVisibleRow] = useState(0);
	const [lastVisibleRow, setLastVisibleRow] = useState(9);
	const [pageSize, setPageSize] = useState(10);
	const [sortField, setSortField] = useState("name");
	const [sortAscending, setSortAscending] = useState(true);
	const [isRemoving, setIsRemoving] = useState(false);
	const [isRemovingId, setIsRemovingId] = useState(0);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	useEffect(() => {
		if (data) {
			let newData = data.filter((item) => {
				if (customer && customer != "All" && item.customerId.toString() != customer) {
					return false;
				}
				if (transporter && transporter != "All" && item.transporterId.toString() != transporter) {
					return false;
				}
				if (vendor && vendor != "All" && item.vendorId.toString() != vendor) {
					return false;
				}
				return true;
			}).slice();

			if (sortField) {
				switch (sortField) {
					case "transporter": {
						newData.sort((a, b) => UtilityService.compareStrings(a.transporterName, b.transporterName));
						break;
					}
					case "vendor": {
						newData.sort((a, b) => UtilityService.compareStrings(a.vendorName, b.vendorName));
						break;
					}
					case "type": {
						newData.sort((a, b) => UtilityService.compareStrings(a.trailerTypeName, b.trailerTypeName));
						break;
					}
					case "number": {
						newData.sort((a, b) => UtilityService.compareStrings(a.trailerNumber, b.trailerNumber));
						break;
					}
					case "location": {
						newData.sort((a, b) => UtilityService.compareStrings(a.location, b.location));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.customerName, b.customerName));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
		}
	}, [data, customer, transporter, vendor, sortField, sortAscending])

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handlePageBack = () => {
		setFirstVisibleRow(firstVisibleRow - pageSize);
		setLastVisibleRow(lastVisibleRow - pageSize);
	}

	const handlePageForward = () => {
		setFirstVisibleRow(firstVisibleRow + pageSize);
		setLastVisibleRow(lastVisibleRow + pageSize);
	}

	const handleRemove =  async (id: number) => {
		if (!dataView.some(t => t.trailerId == id) || isRemoving) return;
		setErrorMessage(undefined);
		setIsRemoving(true);
		setIsRemovingId(id);
		TrailerService.removeTrailer(id.toString())
			.then(() => {
				data.splice(data.indexOf(data.find(t => t.trailerId == id)), 1);
				setDataView(dataView.filter(t => t.trailerId != id));
			})
			.catch((error) => {
				console.log('TrailerService.handleRemove: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsRemovingId(0);
				setIsRemoving(false);
			});
	};

	return (
		<>
		{errorMessage &&
			<Alert className="alert-error" severity="error">{errorMessage}</Alert>
		}
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div>
					<p onClick={() => handleHeaderClick("customer")}>
						Customer
						{" "}
						{(sortField == "customer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "customer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div>
					<p onClick={() => handleHeaderClick("vendor")}>
						Vendor
						{" "}
						{(sortField == "vendor" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "vendor" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div>
					<p onClick={() => handleHeaderClick("transporter")}>
						Transporter
						{" "}
						{(sortField == "transporter" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "transporter" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div>
					<p onClick={() => handleHeaderClick("number")}>
						Number
						{" "}
						{(sortField == "number" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "number" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div>
					<p onClick={() => handleHeaderClick("type")}>
						Type
						{" "}
						{(sortField == "type" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "type" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div>
					<p onClick={() => handleHeaderClick("location")}>
						Location
						{" "}
						{(sortField == "location" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "location" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div></div>
			</div>
			<div className="data">
					{dataView?.length > 0 ?
						(
							dataView?.map((item, index) => (
								<div className={index >= firstVisibleRow && index <= lastVisibleRow ? "row" : "row row-hidden"} key={index}>
									<div><p>{item.customerName}</p></div>
									<div><p>{item.vendorName}</p></div>
									<div><p>{item.transporterName}</p></div>
									<div><p>{item.trailerNumber}</p></div>
									<div><p>{item.trailerTypeName}</p></div>
									<div><p>{item.location}</p></div>
									<div>
										<Button onClick={() => handleRemove(item.trailerId)} disabled={isRemoving}>{isRemoving && isRemovingId == item.trailerId ? "Removing..." : (<><Icons.RemoveItem fill="#a82525" height="16px"></Icons.RemoveItem><span>&nbsp;Remove</span></>)}</Button>
									</div>
									
								</div>
							))
						) :
						(
							<div className="no-results">0 results</div>
						)
					}
			</div>
			<div className="pager">
					<div className="pager-button"><Button variant="outlined"fullWidth={true} disabled={firstVisibleRow == 0 ? true : false} onClick={handlePageBack}>Previous</Button></div>
					<div className="pager-text">{`${firstVisibleRow + 1} to ${(lastVisibleRow + 1) <= dataView.length ? lastVisibleRow + 1 : dataView.length} of ${dataView.length}`}</div>
					<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={lastVisibleRow >= dataView.length - 1 ? true : false} onClick={handlePageForward}>Next</Button></div>
				</div>
		</div>
		</>
	)
}

export default TrailerTable;